import { HomeOutlined, LeftOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Skeleton, Table, Tag, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useSearchParams } from "react-router-dom"

import { CardComponent } from "../../../../components/Card/CardComponent.tsx"
import { generateDocumentTitle } from "../../../../constants/document-title.ts"
import {
  BUDGET_RETURN_PARAM,
  MARKETING_TILE_ANCHOR
} from "../../../../constants/marketing-source-performance.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import { leadSourceIcon } from "../../../Leads/constants/lead-source-icons.tsx"
import { LeadSourceBudgetHistoryDrawer } from "./LeadSourceBudgetHistoryDrawer.tsx"
import { LeadSourceBudgetModal } from "./LeadSourceBudgetModal.tsx"
import { monthLabel } from "./budget-months.ts"
import type { LeadSourceBudgetRow } from "./lead-source-budgets.types.ts"
import { useLeadSourceBudgets } from "./useLeadSourceBudgets.ts"

const { Text, Title } = Typography

/** Only a path inside the app is a place to go back to — never another site. */
const returnPath = (value: string | null): string | null =>
  value && value.startsWith("/") && !value.startsWith("//") ? value : null

/** Each lead source's monthly marketing budget: what is in force, since when,
 *  and what is scheduled, with the edit dialog and the version history. */
export function LeadSourceBudgetPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const backTo = returnPath(searchParams.get(BUDGET_RETURN_PARAM))
  const { budgets, loading, failed, reload } = useLeadSourceBudgets(true)
  const [editing, setEditing] = useState<LeadSourceBudgetRow | null>(null)
  const [history, setHistory] = useState<LeadSourceBudgetRow | null>(null)

  useEffect(() => {
    generateDocumentTitle("Settings Lead Source Budget")
  }, [])

  const rows = Object.values(budgets).sort((a, b) =>
    a.label.localeCompare(b.label, i18n.language)
  )
  const month = (value: string) => monthLabel(value, i18n.language)

  return (
    <div className={"grid w-full"}>
      <Flex className={"min-w-0 overflow-auto"} gap={"small"} vertical>
        <Flex
          align={"flex-start"}
          justify={"space-between"}
          gap={"middle"}
          wrap
        >
          <Flex vertical>
            <Flex align={"center"} gap={8}>
              <Link to={RoutesPaths.settings.templates}>
                <Button
                  aria-label={t("leadSourceBudgets.page.backToTemplates")}
                  icon={<LeftOutlined />}
                  size={"small"}
                />
              </Link>
              <Title className={"m-0 text-start"} level={2}>
                {t("leadSourceBudgets.page.title")}
              </Title>
            </Flex>
            <Text type={"secondary"}>
              {t("leadSourceBudgets.page.subtitle")}
            </Text>
          </Flex>
          {backTo && (
            <Button
              icon={<HomeOutlined />}
              type={"primary"}
              onClick={() => navigate(`${backTo}#${MARKETING_TILE_ANCHOR}`)}
            >
              {t("leadSourceBudgets.page.backHome")}
            </Button>
          )}
        </Flex>

        <CardComponent>
          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : failed ? (
            <Alert
              type={"error"}
              message={t("leadSourceBudgets.errors.load")}
              action={
                <Button size={"small"} onClick={() => void reload()}>
                  {t("leadSourceBudgets.actions.retry")}
                </Button>
              }
            />
          ) : (
            <Table<LeadSourceBudgetRow>
              size={"middle"}
              rowKey={"source"}
              pagination={false}
              dataSource={rows}
              scroll={{ x: "max-content" }}
              columns={[
                {
                  title: t("leadSourceBudgets.columns.source"),
                  key: "source",
                  render: (_, row) => (
                    <Flex gap={10} align={"center"}>
                      <span
                        aria-hidden
                        className={
                          "inline-flex size-7 items-center justify-center rounded-full bg-slate-100"
                        }
                      >
                        {leadSourceIcon(row.source)}
                      </span>
                      <span>{row.label}</span>
                      {!row.is_active && (
                        <Tag style={{ marginInlineEnd: 0 }}>
                          {t("leadSourceBudgets.inactive")}
                        </Tag>
                      )}
                    </Flex>
                  )
                },
                {
                  title: t("leadSourceBudgets.columns.monthlyBudget"),
                  key: "monthly_budget",
                  align: "right",
                  render: (_, row) =>
                    row.current ? (
                      budgetFormat(row.current.amount)
                    ) : (
                      <Text type={"secondary"}>
                        {t("leadSourceBudgets.notSet")}
                      </Text>
                    )
                },
                {
                  title: t("leadSourceBudgets.columns.effective"),
                  key: "effective",
                  render: (_, row) =>
                    row.current ? (
                      month(row.current.effective_month)
                    ) : (
                      <Text type={"secondary"}>—</Text>
                    )
                },
                {
                  title: t("leadSourceBudgets.columns.scheduled"),
                  key: "scheduled",
                  render: (_, row) =>
                    row.scheduled ? (
                      t("leadSourceBudgets.scheduledValue", {
                        month: month(row.scheduled.effective_month),
                        amount: budgetFormat(row.scheduled.amount)
                      })
                    ) : (
                      <Text type={"secondary"}>—</Text>
                    )
                },
                {
                  title: "",
                  key: "actions",
                  render: (_, row) => (
                    <Flex gap={4}>
                      <Button
                        aria-label={`${t("leadSourceBudgets.actions.edit")} — ${row.label}`}
                        size={"small"}
                        type={"link"}
                        onClick={() => setEditing(row)}
                      >
                        {row.current || row.scheduled
                          ? t("leadSourceBudgets.actions.edit")
                          : t("leadSourceBudgets.actions.set")}
                      </Button>
                      <Button
                        aria-label={`${t("leadSourceBudgets.actions.history")} — ${row.label}`}
                        size={"small"}
                        type={"link"}
                        onClick={() => setHistory(row)}
                      >
                        {t("leadSourceBudgets.actions.history")}
                      </Button>
                    </Flex>
                  )
                }
              ]}
            />
          )}
        </CardComponent>
      </Flex>

      {editing && (
        <LeadSourceBudgetModal
          budget={editing}
          onClose={() => setEditing(null)}
          onSaved={reload}
        />
      )}
      {history && (
        <LeadSourceBudgetHistoryDrawer
          budget={budgets[history.source] ?? history}
          onClose={() => setHistory(null)}
          onChanged={reload}
        />
      )}
    </div>
  )
}
